import { apiClient } from './client';

/**
 * 직위 · 직급 마스터 API (인사, migration 138).
 *
 * 서버: `apps/chat-server/src/modules/hr/job-grades.routes.ts`.
 *
 * ★★ **`level` 은 클수록 높다.** m118 이 `users.jobLevel` 의 범위만 정하고 뜻을 안 정했는데,
 *    이 마스터가 그 뜻을 정한다.
 * ★★ **목록 밖 직위를 «틀린 것» 으로 다루지 않는다.** 이미 쓰이는 값이고, 서버는 그 실태를
 *    그대로 돌려준다 — 화면은 담당자가 보고 정리하도록 보여 줄 뿐이다.
 * ★ 옛 서버에는 이 라우트가 없다(404). 부르는 쪽이 실패를 삼킨다.
 */

export interface JobGradeDto {
  id: string;
  name: string;
  /**
   * ★ 부르는 이름 — 직책은 「수석보」인데 부를 때는 「수석」, 「상무보」인데 「상무」(m139).
   *   비어 있으면 `name` 이 곧 호칭이다.
   */
  callName: string | null;
  /** 클수록 높다. */
  level: number;
  active: boolean;
  note: string | null;
  /** 이 직위를 쓰는 재직자 수 — 0 이 아니면 삭제가 막힌다. */
  people: number;
  createdAt: string;
}

export interface UnlistedPositionDto {
  name: string;
  count: number;
}

/** 회사가 «템플릿» 에 보탠 항목 (m139) — 코드의 기본 묶음 **위에 얹는다**. */
export interface GradeTemplateItemDto {
  id: string;
  templateKey: string;
  name: string;
  callName: string | null;
  level: number;
}

export interface LevelChangeDto {
  userId: string;
  name: string;
  position: string;
  /** 지금 값 — `null` 이면 아직 안 매겨졌다. */
  from: number | null;
  to: number;
}

export interface JobGradeBoardDto {
  grades: JobGradeDto[];
  /** 지금 쓰이는데 목록에 없는 이름들 — 이 화면의 첫 쓸모다. */
  unlisted: UnlistedPositionDto[];
  /** 같은 급이 둘 이상인 것 — 막지 않고 알려만 준다. */
  duplicates: Array<{ level: number; names: string[] }>;
  /** 직급 레벨을 맞출 계획(누르기 전이라 아직 안 바뀌었다). */
  plan: LevelChangeDto[];
  summary: {
    total: number;
    active: number;
    /** 직위가 비어 있는 사람. */
    noPosition: number;
    /** 목록 밖 직위를 쓰는 **사람 수**(이름 가짓수가 아니다). */
    unlistedPeople: number;
    planned: number;
  };
  levelRange: { min: number; max: number };
}

export async function fetchJobGrades(): Promise<JobGradeBoardDto> {
  const { data } = await apiClient.get<JobGradeBoardDto>('/hr/job-grades');
  return data;
}

export interface JobGradeInput {
  name: string;
  callName?: string | null;
  level: number;
  active?: boolean;
  note?: string | null;
  /** ★ 동급 직위는 실재한다 — 실수는 막되 «그래도 같게 두겠다» 면 붙인다. */
  allowSameLevel?: boolean;
}

export async function createJobGrade(input: JobGradeInput): Promise<JobGradeDto> {
  const { data } = await apiClient.post<{ grade: JobGradeDto }>('/hr/job-grades', input);
  return data.grade;
}

export async function updateJobGrade(id: string, patch: Partial<JobGradeInput>): Promise<JobGradeDto> {
  const { data } = await apiClient.patch<{ grade: JobGradeDto }>(`/hr/job-grades/${id}`, patch);
  return data.grade;
}

/** 삭제 — **쓰는 사람이 있으면 409**(`GRADE_IN_USE`). 그때는 «접기» 를 쓴다. */
export async function deleteJobGrade(id: string): Promise<void> {
  await apiClient.delete(`/hr/job-grades/${id}`);
}

export interface ApplyLevelsDto {
  applied: boolean;
  changed: number;
  plan: LevelChangeDto[];
}

/**
 * 직급 레벨 맞추기.
 *
 * ★★ **`dryRun` 이면 아무것도 안 바꾸고 계획만** 돌려준다. 사람의 값을 바꾸는 일이라
 *    화면이 먼저 «누가 무엇에서 무엇으로» 를 보여 주고 담당자가 누른다.
 */
export async function applyJobLevels(dryRun: boolean): Promise<ApplyLevelsDto> {
  const { data } = await apiClient.post<ApplyLevelsDto>('/hr/job-grades/apply-levels', { dryRun });
  return data;
}

/**
 * 순위 바꾸기 — 위에서부터의 차례를 보낸다.
 *
 * ★★ **레벨을 새로 만들지 않는다.** 서버가 보낸 직위들이 지금 쓰는 레벨 값을 큰 것부터 모아
 *    차례대로 다시 나눠 준다. 그래서 순위를 아무리 바꿔도 범위(1~20) 밖으로 못 나가고,
 *    같은 급이던 둘은 같은 급으로 남는다.
 * ★ 일부만 보내도 된다 — 화면은 «접기» 안 한 것만 보낸다.
 */
export async function reorderJobGrades(ids: string[]): Promise<{ changed: number }> {
  const { data } = await apiClient.post<{ changed: number }>('/hr/job-grades/reorder', { ids });
  return data;
}

/** 회사가 보탠 템플릿 항목 — 옛 서버에는 없다(404). 부르는 쪽이 빈 목록으로 받는다. */
export async function fetchGradeTemplateItems(): Promise<GradeTemplateItemDto[]> {
  const { data } = await apiClient.get<{ items: GradeTemplateItemDto[] }>('/hr/job-grades/templates');
  return data.items;
}

export async function addGradeTemplateItem(input: {
  templateKey: string; name: string; callName?: string | null; level: number;
}): Promise<GradeTemplateItemDto> {
  const { data } = await apiClient.post<{ item: GradeTemplateItemDto }>('/hr/job-grades/templates', input);
  return data.item;
}

export async function removeGradeTemplateItem(id: string): Promise<void> {
  await apiClient.delete(`/hr/job-grades/templates/${id}`);
}

/**
 * ★★ **목록 밖 직위를 마스터에 담는다** (2026-08-30 · 사용자 지적).
 *
 * 담자마자 그 이름을 쓰는 사람의 등급이 함께 맞춰진다 — 담기만 하면 「담았는데 그대로」다.
 */
export async function adoptJobGrade(body: {
  name: string; level: number; callName?: string | null;
}): Promise<{ grade: JobGradeDto; syncedUsers: number }> {
  const { data } = await apiClient.post('/hr/job-grades/adopt', body);
  return data;
}

/**
 * ⚠ **사람의 값을 지운다** — 그 직위를 쓰는 사람의 직위·등급을 비운다.
 * `dryRun` 으로 먼저 몇 명인지 세어 보여 준 뒤에만 누른다.
 */
export async function clearUnlistedPositions(names: string[], dryRun = false): Promise<{
  applied: boolean; cleared: number; names: string[];
}> {
  const { data } = await apiClient.post('/hr/job-grades/clear', { names, dryRun });
  return data;
}
